import * as React from 'react';
import { Checkbox, FormControlLabel, Menu, MenuItem, Typography } from '@mui/material';
import { MenuItemProps } from '@mui/material/MenuItem';

export interface MuiMenuItem {
    label?: string;
    render?: (props: MenuItemProps & { key: string | number }) => React.ReactNode;
    onClick?: () => void;
    color?: 'error' | 'secondary' | 'primary';
    isChecked?: boolean;
    onChange?: (e: any) => void;
    disabled?: boolean;
}

type MenuOpenEventHandler = any;
export interface MenuOnProps {
    renderOn: (onOpen: MenuOpenEventHandler) => React.ReactNode;
    variant?: 'withCheckbox';
    items?: MuiMenuItem[];
    children?: React.ReactNode;
}

export const MenuOn: React.FC<MenuOnProps> = ({ renderOn, items, children: _children, variant, ...rest }) => {
    const [menuAnchorElement, setMenuAnchorElement] = React.useState<HTMLElement | undefined>(undefined);

    const isMenuOpen = menuAnchorElement !== undefined;

    const onOpen = React.useCallback((event: React.SyntheticEvent<any>) => {
        if (event) {
            if (event.stopPropagation) {
                event.stopPropagation();
            }
            setMenuAnchorElement(event.currentTarget);
        }
    }, []);

    const close = React.useCallback((event: React.SyntheticEvent<any>) => {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        setMenuAnchorElement(undefined);
    }, []);

    const renderDefaultItems = React.useCallback(
        () =>
            items &&
            items.map((item, index) => {
                const { onClick, render, color, ...itemRest } = item;

                const props = {
                    onClick,
                    key: index,
                    children: <Typography color={color || 'inherit'}>{item.label}</Typography>,
                    ...itemRest
                };
                if (render) {
                    return render({ onClick, key: index, ...itemRest });
                }

                return React.isValidElement(item)
                    ? React.cloneElement(item, props)
                    : React.createElement(MenuItem, props);
            }),
        [items]
    );

    const renderCheckboxItems = React.useCallback(
        () =>
            items &&
            items.map((item, index) => {
                const { onClick: _onClick, onChange: _onChange, isChecked, ...itemRest } = item;

                const children = (
                    <FormControlLabel
                        label={item.label}
                        control={
                            <Checkbox
                                color="primary"
                                name={item.label}
                                checked={isChecked}
                                onChange={item.onChange}
                                disabled={item.disabled}
                            />
                        }
                        disabled={item.disabled}
                    />
                );
                const props = {
                    children,
                    key: index,
                    ...itemRest
                };

                return React.isValidElement(item)
                    ? React.cloneElement(item, props)
                    : React.createElement(MenuItem, props);
            }),
        [items]
    );

    const renderItems = React.useCallback(() => {
        if (variant === 'withCheckbox') {
            return renderCheckboxItems();
        }
        return renderDefaultItems();
    }, [variant, renderCheckboxItems, renderDefaultItems]);

    return (
        <React.Fragment>
            {renderOn(onOpen)}
            <Menu
                onClick={variant !== 'withCheckbox' ? close : undefined}
                anchorEl={menuAnchorElement}
                open={isMenuOpen}
                onClose={close}
                {...rest}
            >
                {renderItems()}
            </Menu>
        </React.Fragment>
    );
};
